import React, { lazy, Suspense, useEffect, useMemo, useState } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { RootState, storeActions } from '../../store'
import useShowEmergency from '../../hooks/useShowEmergency'
import useShowMap from '../../hooks/useShowMap'
import { CurrentSubView } from '../../models/enums'

const HistoryList = lazy(() => import('../History/HistoryList'))
const LanguageChoice = lazy(() => import('../Language/LanguageChoice'))
const AttachmentDetails = lazy(() => import('../Attachments/AttachmentDetail'))
const FullScreenImage = lazy(() => import('../Attachments/Utils/FullScreenImage'))
const Weather = lazy(() => import('../Weather/Weather'))
const CustomView = lazy(() => import('./CustomView'))
const Emergency = lazy(() => import('../Emergency/Emergency'))
const Map = lazy(() => import('../Map/MapContainer'))

const SubViews = ({ externalComponents }): JSX.Element => {
  const dispatch = useDispatch()
  const locale = useSelector((state: RootState) => state.localeReducer.locale)
  const currentSubView = useSelector((state: RootState) => state.utilsReducer.currentSubView)
  const currentCustomView = useSelector((state: RootState) => state.utilsReducer.currentCustomView)
  const enabledFeatures = useSelector((state: RootState) => state.retorikReducer.enabledFeatures)

  const showMap = useShowMap()
  const showEmergency = useShowEmergency()

  const [displayCustomComponent, setDisplayCustomComponent] = useState<boolean>(true)
  const [customView, setCustomView] = useState<{ view: string; title: string; icon?: JSX.Element } | undefined>(undefined)

  useEffect(() => {
    if (currentCustomView) {
      let label = ''
      if (currentCustomView.label && typeof currentCustomView.label === 'string') {
        label = currentCustomView.label
      } else if (currentCustomView.label && typeof currentCustomView.label === 'object') {
        label = currentCustomView.label[locale]
      }
      if (label && currentCustomView.customDisplay && currentCustomView.icon) {
        setCustomView({
          view: currentCustomView.customDisplay,
          title: label,
          icon: currentCustomView.icon
        })
      }
      dispatch(storeActions.utils.setCurrentSubView(CurrentSubView.custom))
    }
  }, [currentCustomView])

  useEffect(() => {
    showMap && dispatch(storeActions.utils.setCurrentSubView(CurrentSubView.map))
  }, [showMap])

  useEffect(() => {
    showEmergency && dispatch(storeActions.utils.setCurrentSubView(CurrentSubView.emergency))
  }, [showEmergency])

  useEffect(() => {
    dispatch(storeActions.utils.unlockClickedButton())
    setDisplayCustomComponent(currentSubView === CurrentSubView.custom)
  }, [currentSubView])

  const processedSubView = useMemo<JSX.Element>(() => {
    switch (currentSubView) {
      case CurrentSubView.none:
      case CurrentSubView.custom:
        return <React.Fragment />
      case CurrentSubView.history:
        return <HistoryList />
      case CurrentSubView.languages:
        return <LanguageChoice />
      case CurrentSubView.emergency:
        if (enabledFeatures.includes('emergency')) {
          return <Emergency />
        } else {
          console.log('Retorik Framework > tried to switch to emergency while the feature is not activated in Retorik Studio')
        }
        break
      case CurrentSubView.weather:
        if (enabledFeatures.includes('weather')) {
          return <Weather />
        } else {
          console.log('Retorik Framework > tried to switch to weather while the feature is not activated in Retorik Studio')
        }
        break
      case CurrentSubView.map:
        if (enabledFeatures.includes('hospitality')) {
          return <Map />
        } else {
          console.log('Retorik Framework > tried to switch to interactive map while the feature is not activated in Retorik Studio')
        }
        break
    }

    return <React.Fragment />
  }, [currentSubView, enabledFeatures])

  return (
    <Suspense>
      {processedSubView}

      <CustomView
        display={displayCustomComponent}
        externalComponents={externalComponents}
        customView={customView?.view}
        title={customView?.title}
        icon={customView?.icon}
        setDisplayCustomComponent={setDisplayCustomComponent}
      />

      <AttachmentDetails />
      <FullScreenImage />
    </Suspense>
  )
}

export default SubViews
